import { useCallback } from 'react';
import { Button } from '@shared/components/Button';
import { useToast } from '@shared/components/toast';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { useDispatch } from '@shared/store';
import { bounceIn, grid } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import * as Actions from '../store/actions';
import { MAX_CHAT_ATTACHMENTS } from '../store/reducer';

type ValidateUploadFn = ReturnType<typeof useValidateUploads>;

interface Props {
  draftId: string;
  validateUploads: ValidateUploadFn;
}

export function MBAttachmentButton({ draftId, validateUploads }: Props) {
  const dispatch = useDispatch();
  const toast = useToast();
  const isDesktop = useIsDesktop();

  const onLimitExceeded = useCallback(
    (rejectedCount: number) => {
      toast.show({
        style: 'warning',
        message: i18n('chat.input.attachmentLimit.exceeded', {
          max: MAX_CHAT_ATTACHMENTS,
          count: rejectedCount,
        }),
        duration: 4000,
      });
    },
    [toast],
  );

  const onPress = useCallback(() => {
    dispatch(Actions.selectAttachment('all', draftId, validateUploads, onLimitExceeded));
    MBAnalytics.track('Chat::Input::SelectAttachment', { draftId });
  }, [dispatch, draftId, validateUploads, onLimitExceeded]);

  return (
    <Button
      variant="ghost"
      size="large"
      onClick={onPress}
      icon={isDesktop ? 'imagestack' : 'camera'}
      iconSize={grid(5.5)}
      iconOnly
      className={bounceIn}
    />
  );
}
